import { useCallback, useReducer } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Box } from '@mui/material';
import { SlideshowInfinite, useItems } from '@scorenco/components';
import { useRouter } from 'next/router';

import { SponsorItem } from '../SponsorsWidget/_internal/SponsorItem';
import { GetClubSponsorsDocument } from './_internal/Sponsors.query.generated';

type SponsorsSliderWidgetProps = {
  clubId: number;
};

export const SponsorsSliderWidget = ({ clubId }: SponsorsSliderWidgetProps) => {
  const router = useRouter();
  const speed = router.query.speed ? Number(router.query.speed) : 1;
  const height = router.query.height ? Number(router.query.height) : '8';
  const width = router.query.width ? Number(router.query.width) : '8';
  const categoryId = router.query.category
    ? Number(router.query.category)
    : undefined;
  const hasCategory = !!categoryId;
  const cacheTTL = router.query.noCache ? 0 : undefined;

  const { data } = useQuery(GetClubSponsorsDocument, {
    variables: {
      clubId,
      categoryId: categoryId ?? 0,
      hasCategory,
      cacheTTL,
    },
    context: {
      role: 'anonymous',
    },
    skip: !clubId,
  });

  const sponsors = useItems(
    hasCategory
      ? data?.sponsors_sponsor_by_category
      : data?.sponsors_sponsor
  );

  const shuffleReducer = useCallback(
    (_prev: typeof sponsors, action: { sponsors: typeof sponsors }) =>
      [...action.sponsors].sort(() => Math.random() - 0.5),
    []
  );

  const [shuffledSponsors, shuffleSponsors] = useReducer(
    shuffleReducer,
    sponsors,
    (initial) => [...initial].sort(() => Math.random() - 0.5)
  );

  if (sponsors.length !== shuffledSponsors.length && sponsors.length > 0) {
    shuffleSponsors({ sponsors });
  }

  return (
    <Box
      css={css`
        width: 100%;
        height: 10rem;
        margin-bottom: 1.5rem;
        background: transparent;
      `}
    >
      <SlideshowInfinite
        length={sponsors.length}
        elementRemSize={2 + Number(width)}
        speed={speed}
      >
        {shuffledSponsors.map((sponsor, index) =>
          sponsor ? (
            <SponsorItem
              key={sponsor.id ?? index}
              sponsor={sponsor}
              loading="eager"
              imageSx={{
                height: `${height}rem`,
                width: `${width}rem`,
                objectFit: 'contain',
                margin: '0 1rem',
              }}
            />
          ) : null
        )}
      </SlideshowInfinite>
    </Box>
  );
};
